/**
 * @file: ArchivedThreadsView.tsx
 *
 * The archived-threads surface: a flat, paginated list of the user's
 * archived feed threads. Swipe a row to restore it (un-archive); tap to open
 * the thread detail. Archived threads also resurface in the main inbox on
 * their own when new activity arrives.
 */

import { feedThreadTitle } from '@euler/app/flows/feed/utils/feedThreadTitle';
import { useInboxNavigation } from '@euler/app/flows/inbox/functions/useInboxNavigation';
import { ListEmptyState } from '@euler/components/ListEmptyState';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FeedThreadInboxItem } from '@euler/model/feed';
import { useServiceFactory } from '@euler/services/factory';
import { FC, memo, useCallback } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  RefreshControl,
  View,
} from 'react-native';
import { useArchivedFeedThreads } from '../functions/useArchivedFeedThreads';
import { ArchivedSwipeableRow } from './ArchivedSwipeableRow';

export const ArchivedThreadsView: FC = memo(() => {
  const { rpx } = useResponsiveContext();
  const navigation = useInboxNavigation();
  const { feedService } = useServiceFactory();
  const {
    threads,
    loading,
    refreshing,
    hasMore,
    loadingMore,
    refresh,
    loadMore,
    removeThread,
  } = useArchivedFeedThreads();

  const onOpen = useCallback(
    (thread: FeedThreadInboxItem) => {
      navigation.navigate('_feedThread', {
        threadId: thread.id,
        scope: thread.scope,
        scopeId: thread.scopeId,
        title: feedThreadTitle(thread),
      });
    },
    [navigation],
  );

  const onRestore = useCallback(
    (thread: FeedThreadInboxItem) => {
      // Optimistic: drop the row, reconcile from the server only on failure.
      removeThread(thread.id);
      feedService
        .archiveThread(thread.id, false)
        .catch(() => void refresh({ silent: true }));
    },
    [feedService, removeThread, refresh],
  );

  const renderItem: ListRenderItem<FeedThreadInboxItem> = useCallback(
    ({ item }) => (
      <ArchivedSwipeableRow
        thread={item}
        onPress={() => onOpen(item)}
        onRestore={onRestore}
      />
    ),
    [onOpen, onRestore],
  );

  if (loading && threads.length === 0) {
    return (
      <View
        css={`
          flex: 1;
          align-items: center;
          justify-content: center;
        `}
      >
        <ActivityIndicator />
      </View>
    );
  }

  if (threads.length === 0) {
    return (
      <ListEmptyState title="暂无已归档" subtitle="归档的会话会出现在这里" />
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: '#fff' }}>
      <FlatList
        data={threads}
        keyExtractor={t => t.id}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={refresh} />
        }
        onEndReachedThreshold={0.5}
        onEndReached={hasMore ? loadMore : undefined}
        ListFooterComponent={
          loadingMore ? (
            <View
              css={`
                padding: ${rpx(16)}px 0;
              `}
            >
              <ActivityIndicator />
            </View>
          ) : null
        }
        contentContainerStyle={{ flexGrow: 1 }}
      />
    </View>
  );
});
